import { Dialog, Transition } from "@headlessui/react";
import {
  Dispatch,
  Fragment,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslations } from "next-intl";
import SelectProductToReview from "./components/SelectProductToReview";
import { Option } from "src/types/global/Option";
import ReviewerMeasurementsForm from "./components/ReviewerMeasurementsForm";
import ReviewForm from "./components/ReviewForm";
import WhichDaysWornForm from "./components/WhichDaysWornForm";
import FitForm from "./components/FitForm";
import RatingForm from "./components/RatingForm";
import PeriodManagementOptionsForm from "./components/PeriodManagementOptionsForm";
import TimeAsShemsiUserForm from "./components/TimeAsShemsiUserForm";
import ReviewerInformationForm from "./components/ReviewerInformationForm";
import ReviewPreview from "./components/ReviewPreview";
import {
  ReviewTab,
  ValueWithImage,
  reviewTabsData,
} from "src/config/reviewSystemData";
import ModalButtons from "./components/ModalButtons";
import * as EmailValidator from "email-validator";
import handleReviewStepChange from "src/helpers/handleReviewStepChange";
import axios from "axios";
import ReviewStepTabs from "./components/ReviewStepTabs";
import classNames from "src/helpers/classNames";

interface ReviewFormModalProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  product: string;
}

export interface ReviewFormErrors {
  rating: string;
  fit: string;
  daysWorn: string;
  review: string;
  periodManagementOptions: string;
  timeAsShemsiUser: string;
  reviewerFullname: string;
  reviewerEmail: string;
  sizePurchased: string;
}

const ReviewFormModal = ({ open, setOpen, product }: ReviewFormModalProps) => {
  const t = useTranslations("ReviewForm");

  const [reviewStep, setReviewStep] = useState(0);
  const [productToReview, setProductToReview] = useState<Option>({
    label: product.charAt(0).toUpperCase() + product.slice(1),
    value: product,
  });
  const [rating, setRating] = useState(0);
  const [fit, setFit] = useState<ValueWithImage>({
    value: "",
    image: { src: "", width: 0, height: 0 },
  });
  const [daysWorn, setDaysWorn] = useState<{ value: string; image: string }[]>(
    []
  );
  const [reviewTitle, setReviewTitle] = useState("");
  const [reviewBody, setReviewBody] = useState("");
  const [height, setHeight] = useState("");
  const [weight, setWeight] = useState("");
  const [pantSize, setPantSize] = useState<Option>({
    label: t("select_your_pants_size"),
    value: "",
  });
  const [sizePurchased, setSizePurchased] = useState<Option>({
    label: t("select_purchased_size"),
    value: "",
  });
  const [periodManagementOptions, setPeriodManagementOptions] = useState<
    ValueWithImage[]
  >([]);
  const [timeAsShemsiUser, setTimeAsShemsiUser] = useState<string>("");
  const [reviewerFullname, setReviewerFullname] = useState("");
  const [reviewerAge, setReviewerAge] = useState("");
  const [reviewerEmail, setReviewerEmail] = useState("");
  const [reviewTabs, setReviewTabs] = useState<ReviewTab[]>(reviewTabsData);
  const [formErrors, setFormErrors] = useState<ReviewFormErrors>({
    rating: "",
    fit: "",
    daysWorn: "",
    review: "",
    periodManagementOptions: "",
    timeAsShemsiUser: "",
    reviewerFullname: "",
    reviewerEmail: "",
    sizePurchased: "",
  });
  const [isFormSubmittedOnce, setIsFormSubmittedOnce] = useState(false);
  const [loadingSubmission, setLoadingSubmission] = useState(false);
  const [reviewFormStatus, setReviewFormStatus] = useState({
    message: "",
    success: false,
  });

  const renderBackButton = reviewStep > 0 && reviewStep < reviewTabs.length + 1;

  const cancelButtonRef = useRef(null);

  const renderReviewStep = (step: number) => {
    switch (step) {
      case 0:
        return (
          <SelectProductToReview
            productToReview={productToReview}
            setProductToReview={setProductToReview}
            setReviewStep={setReviewStep}
          />
        );
      case 1:
        return (
          <RatingForm
            rating={rating}
            setRating={setRating}
            setReviewStep={setReviewStep}
            isFormSubmittedOnce={isFormSubmittedOnce}
          />
        );
      case 2:
        return (
          <FitForm
            fit={fit}
            setFit={setFit}
            setReviewStep={setReviewStep}
            isFormSubmittedOnce={isFormSubmittedOnce}
          />
        );
      case 3:
        return (
          <WhichDaysWornForm
            daysWorn={daysWorn}
            setDaysWorn={setDaysWorn}
            setReviewStep={setReviewStep}
            reviewFormErrors={formErrors}
            isFormSubmittedOnce={isFormSubmittedOnce}
          />
        );
      case 4:
        return (
          <ReviewForm
            reviewTitle={reviewTitle}
            setReviewTitle={setReviewTitle}
            reviewBody={reviewBody}
            setReviewBody={setReviewBody}
            setReviewStep={setReviewStep}
            reviewFormErrors={formErrors}
            isFormSubmittedOnce={isFormSubmittedOnce}
          />
        );
      case 5:
        return (
          <ReviewerMeasurementsForm
            height={height}
            setHeight={setHeight}
            weight={weight}
            setWeight={setWeight}
            pantSize={pantSize}
            setPantSize={setPantSize}
            sizePurchased={sizePurchased}
            setSizePurchased={setSizePurchased}
            setReviewStep={setReviewStep}
            isFormSubmittedOnce={isFormSubmittedOnce}
          />
        );
      case 6:
        return (
          <PeriodManagementOptionsForm
            periodManagementOptions={periodManagementOptions}
            setPeriodManagementOptions={setPeriodManagementOptions}
            setReviewStep={setReviewStep}
            isFormSubmittedOnce={isFormSubmittedOnce}
          />
        );
      case 7:
        return (
          <TimeAsShemsiUserForm
            timeAsShemsiUser={timeAsShemsiUser}
            setTimeAsShemsiUser={setTimeAsShemsiUser}
            setReviewStep={setReviewStep}
            isFormSubmittedOnce={isFormSubmittedOnce}
          />
        );
      case 8:
        return (
          <ReviewerInformationForm
            reviewerFullname={reviewerFullname}
            setReviewerFullname={setReviewerFullname}
            reviewerAge={reviewerAge}
            setReviewerAge={setReviewerAge}
            reviewerEmail={reviewerEmail}
            setReviewerEmail={setReviewerEmail}
            setReviewStep={setReviewStep}
          />
        );
      case 9:
        return (
          <ReviewPreview
            productToReview={productToReview.value}
            rating={rating}
            fit={fit}
            daysWorn={daysWorn}
            reviewTitle={reviewTitle}
            reviewBody={reviewBody}
            height={height}
            weight={weight}
            pantSize={pantSize.value}
            sizePurchased={sizePurchased.value}
            periodManagementOptions={periodManagementOptions}
            timeAsShemsiUser={timeAsShemsiUser}
            reviewerName={reviewerFullname}
            reviewerAge={reviewerAge}
            reviewerEmail={reviewerEmail}
            setReviewStep={setReviewStep}
            loadingSubmission={loadingSubmission}
            reviewFormErrors={formErrors}
            reviewFormStatus={reviewFormStatus}
          />
        );
      default:
        break;
    }
  };

  function validateForm(
    setFormErrors: Dispatch<SetStateAction<ReviewFormErrors>>,
    t: (string: string) => string
  ) {
    // rating
    let ratingError = "";
    if (rating < 1) {
      ratingError = t("rating_is_required");
    }
    // fit
    let fitError = "";
    if (!fit.value) {
      fitError = t("fit_is_required");
    }
    // days worn on
    let daysWornError = "";
    if (daysWorn.length < 1) {
      daysWornError = t("days_worn_is_required");
    }
    // review title and body
    let reviewError = "";
    if (!reviewTitle && !reviewBody) {
      reviewError = t("review_title_and_review_body_is_required");
    } else if (!reviewTitle) {
      reviewError = t("review_title_is_required");
    } else if (!reviewBody) {
      reviewError = t("review_body_is_required");
    }
    // purchased size
    let sizePurchasedError = "";
    if (!sizePurchased) {
      sizePurchasedError = t("size_purchased_is_required");
    }
    // period management options
    let periodManagementOptionsError = "";
    if (periodManagementOptions.length < 1) {
      periodManagementOptionsError = t("period_management_options_is_required");
    }
    // time as shemsi user
    let timeAsShemsiUserError = "";
    if (!timeAsShemsiUser) {
      timeAsShemsiUserError = t("time_as_shemsi_user_is_required");
    }
    // full name
    let fullnameError = "";
    if (!reviewerFullname || reviewerFullname.trim().split(" ").length < 2) {
      fullnameError = t("fullname_is_required");
    }
    // email
    let emailError = "";
    if (!reviewerEmail) {
      emailError = t("email_is_required");
    } else if (!EmailValidator.validate(reviewerEmail)) {
      emailError = t("validate_email");
    }

    // setting the errors
    setFormErrors((prevState) => ({
      ...prevState,
      rating: ratingError,
      fit: fitError,
      daysWorn: daysWornError,
      review: reviewError,
      periodManagementOptions: periodManagementOptionsError,
      timeAsShemsiUser: timeAsShemsiUserError,
      reviewerFullname: fullnameError,
      reviewerEmail: emailError,
      sizePurchased: sizePurchasedError,
    }));

    return ![
      ratingError,
      fitError,
      daysWornError,
      reviewError,
      periodManagementOptionsError,
      timeAsShemsiUserError,
      fullnameError,
      emailError,
      sizePurchasedError,
    ].some(Boolean);
  }

  const valuesOnlyArray = (arr: any[]): string[] => {
    return arr.map((item) => item.value);
  };

  const handleValidateAndSubmitReview = async (e: any) => {
    e.preventDefault();
    setIsFormSubmittedOnce(true);
    setLoadingSubmission(true);
    const isFormValid = validateForm(setFormErrors, t);

    if (!isFormValid) {
      setLoadingSubmission(false);
      return;
    }

    try {
      const res = await axios.post(
        `${process.env.NEXT_PUBLIC_FULFILLMENT_CONNECTOR_URL}/reviews`,
        {
          preferredStyle: productToReview.value,
          rating: rating,
          sizeFeedback: fit.value,
          wornOn: valuesOnlyArray(daysWorn),
          reviewTitle: reviewTitle,
          reviewBody: reviewBody,
          height: height,
          weight: weight,
          pantSize: pantSize.value,
          sizePurchased: sizePurchased.value,
          preferredPeriodProduct: valuesOnlyArray(periodManagementOptions),
          userSince: timeAsShemsiUser,
          age: reviewerAge,
          fullName: reviewerFullname,
          email: reviewerEmail,
        }
      );
      const json = await res.data;
      setLoadingSubmission(false);
      if (json.status) {
        setReviewFormStatus({
          message: t("review_successfuly_submitted"),
          success: true,
        });
      }
    } catch (error) {
      setLoadingSubmission(false);
      setReviewFormStatus({
        message: "Error: " + error,
        success: false,
      });
      console.log("review error", error);
    }
  };

  useEffect(() => {
    handleReviewStepChange(reviewStep, setReviewTabs);
  }, [reviewStep]);

  useEffect(
    function validateInputsOnChange() {
      if (isFormSubmittedOnce) {
        validateForm(setFormErrors, t);
      }
    },
    [
      isFormSubmittedOnce,
      reviewTitle,
      reviewBody,
      daysWorn,
      periodManagementOptions,
      reviewerFullname,
      reviewerEmail,
    ]
  );

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-40"
        initialFocus={cancelButtonRef}
        onClose={setOpen}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-700 bg-opacity-60 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10">
          <div className="flex min-h-full flex-grow justify-center py-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className={classNames(
                  "relative w-5/6 transform self-center bg-white shadow-xl transition-all sm:my-8 lg:w-1/2",
                  reviewStep === reviewTabs.length + 1
                    ? "overflow-auto"
                    : "overflow-visible"
                )}
              >
                <form
                  onSubmit={handleValidateAndSubmitReview}
                  className="flex h-[600px] flex-col items-center p-8"
                >
                  {/* Back and close buttons */}
                  <ModalButtons
                    setOpen={setOpen}
                    renderBackButton={renderBackButton}
                    setReviewStep={setReviewStep}
                  />
                  {/* Review form step */}
                  {renderReviewStep(reviewStep)}
                </form>
                {/* Review form step tabs */}
                {reviewStep > 0 && reviewStep < reviewTabs.length + 1 && (
                  <ReviewStepTabs
                    reviewTabs={reviewTabs}
                    setReviewStep={setReviewStep}
                  />
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ReviewFormModal;
